// src/pages/InformeAuditoria.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import {
  getAuditoria,
  getAuditorias,
} from "../helpers/rutaAuditoria";

import logo from "../images/ridersmiami-logo.png";

/* -------------------------------------------------------------------------- */
/*                                  Utilidades                                */
/* -------------------------------------------------------------------------- */

const todayLocalISO = () => {
  const d = new Date();

  d.setMinutes(
    d.getMinutes() - d.getTimezoneOffset()
  );

  return d.toISOString().slice(0, 10);
};

const firstDayMonthISO = () => {
  const d = new Date();

  d.setDate(1);

  d.setMinutes(
    d.getMinutes() - d.getTimezoneOffset()
  );

  return d.toISOString().slice(0, 10);
};

const sumarDiasISO = (fechaISO, cantidad = 0) => {
  if (!fechaISO) return "";

  const [anio, mes, dia] = String(fechaISO)
    .split("-")
    .map(Number);

  if (!anio || !mes || !dia) return fechaISO;

  const fecha = new Date(
    Date.UTC(anio, mes - 1, dia)
  );

  fecha.setUTCDate(
    fecha.getUTCDate() + cantidad
  );

  return fecha.toISOString().slice(0, 10);
};

const clean = (value) =>
  String(value ?? "").trim();

const formatDateTime = (value) => {
  if (!value) return "-";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) {
    return "-";
  }

  return d.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const formatDateTimePdf = (value) => {
  if (!value) return "-";

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) {
    return "-";
  }

  return d.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const cargarImagen = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();

    img.crossOrigin = "Anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const parsearJson = (value) => {
  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return null;
  }

  if (typeof value === "object") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const jsonBonito = (value) => {
  const parsed = parsearJson(value);

  if (parsed === null) {
    return "Sin información";
  }

  if (typeof parsed === "string") {
    return parsed;
  }

  return JSON.stringify(parsed, null, 2);
};

const mostrarValor = (value) => {
  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return "-";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

const accionBadge = (accion) => {
  const value = clean(accion).toUpperCase();

  if (
    value === "ALTA" ||
    value === "CREACION"
  ) {
    return "success";
  }

  if (
    value === "MODIFICACION" ||
    value === "ACTUALIZACION"
  ) {
    return "primary";
  }

  if (value === "ANULACION") {
    return "warning";
  }

  if (
    value === "ELIMINACION" ||
    value === "BAJA"
  ) {
    return "danger";
  }

  return "secondary";
};

const resultadoBadge = (resultado) => {
  const value = clean(resultado).toUpperCase();

  if (value === "OK") return "success";
  if (value === "ERROR") return "danger";

  return "secondary";
};

const rolTexto = (role) => {
  const value = clean(role).toUpperCase();

  if (value === "ROOT_ROLE") {
    return "ROOT";
  }

  if (value === "ADMIN_ROLE") {
    return "ADMIN";
  }

  if (value === "VENDEDOR_ROLE") {
    return "VENDEDOR";
  }

  return value || "-";
};

const escaparCSV = (value) => {
  const text = String(value ?? "");

  return `"${text.replace(/"/g, '""')}"`;
};

/* -------------------------------------------------------------------------- */
/*                                 Componente                                 */
/* -------------------------------------------------------------------------- */

export default function InformeAuditoria() {
  /* -------------------------------- Filtros ------------------------------- */

  const [desde, setDesde] = useState(
    firstDayMonthISO()
  );

  const [hasta, setHasta] = useState(
    todayLocalISO()
  );

  const [usuario, setUsuario] = useState("");
  const [modulo, setModulo] = useState("");
  const [accion, setAccion] = useState("");
  const [entidad, setEntidad] = useState("");
  const [resultado, setResultado] = useState("");
  const [q, setQ] = useState("");

  /* -------------------------------- Datos -------------------------------- */

  const [auditorias, setAuditorias] =
    useState([]);

  const [
    usuariosDisponibles,
    setUsuariosDisponibles,
  ] = useState([]);

  const [resumen, setResumen] = useState({
    total: 0,
    altas: 0,
    modificaciones: 0,
    anulaciones: 0,
    eliminaciones: 0,
    errores: 0,
  });

  /* ------------------------------- Paginación ----------------------------- */

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  /* ------------------------------- Estados UI ----------------------------- */

  const [loading, setLoading] = useState(false);

  const [exportando, setExportando] =
    useState(false);

  const [error, setError] = useState("");

  /* -------------------------------- Modal -------------------------------- */

  const [modalVisible, setModalVisible] =
    useState(false);

  const [
    detalleLoading,
    setDetalleLoading,
  ] = useState(false);

  const [
    auditoriaDetalle,
    setAuditoriaDetalle,
  ] = useState(null);

  const [detalleError, setDetalleError] =
    useState("");

  /* ------------------------------------------------------------------------ */
  /*                                  Filtros                                 */
  /* ------------------------------------------------------------------------ */

  const obtenerFiltrosActuales = () => ({
    desde,

    /*
      El backend compara el cierre en UTC.
      Se envía internamente el día siguiente
      para incluir toda la jornada argentina.
      En pantalla continúa mostrándose la fecha elegida.
    */
    hasta: hasta
      ? sumarDiasISO(hasta, 1)
      : "",

    usuario: clean(usuario),
    modulo: clean(modulo).toUpperCase(),
    accion: clean(accion).toUpperCase(),
    entidad: clean(entidad).toUpperCase(),
    resultado: clean(resultado).toUpperCase(),
    q: clean(q),
  });

  /* ------------------------------------------------------------------------ */
  /*                                Carga datos                               */
  /* ------------------------------------------------------------------------ */

  const cargarAuditorias = async ({
    pagina = 1,
    limite = limit,
    filtros = obtenerFiltrosActuales(),
  } = {}) => {
    setLoading(true);
    setError("");

    try {
      const resp = await getAuditorias({
        page: pagina,
        limit: limite,
        ...filtros,
      });

      setAuditorias(
        Array.isArray(resp?.auditorias)
          ? resp.auditorias
          : []
      );

      setTotal(
        Number(resp?.total || 0)
      );

      setResumen({
        total: Number(
          resp?.resumen?.total ??
            resp?.total ??
            0
        ),

        altas: Number(
          resp?.resumen?.altas || 0
        ),

        modificaciones: Number(
          resp?.resumen?.modificaciones || 0
        ),

        anulaciones: Number(
          resp?.resumen?.anulaciones || 0
        ),

        eliminaciones: Number(
          resp?.resumen?.eliminaciones || 0
        ),

        errores: Number(
          resp?.resumen?.errores || 0
        ),
      });

      setUsuariosDisponibles(
        Array.isArray(resp?.usuarios)
          ? resp.usuarios
          : []
      );

      setPage(
        Number(resp?.page || pagina)
      );

      setLimit(
        Number(resp?.limit || limite)
      );
    } catch (err) {
      console.error(
        "Error cargando auditoría:",
        err
      );

      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo consultar la auditoría."
      );

      setAuditorias([]);
      setUsuariosDisponibles([]);
      setTotal(0);

      setResumen({
        total: 0,
        altas: 0,
        modificaciones: 0,
        anulaciones: 0,
        eliminaciones: 0,
        errores: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarAuditorias({
      pagina: 1,
      limite: 50,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------------------------------------------------------------ */
  /*                            Datos derivados                               */
  /* ------------------------------------------------------------------------ */

  const totalPaginas = Math.max(
    1,
    Math.ceil(total / limit)
  );

  const desdeRegistro =
    total === 0
      ? 0
      : (page - 1) * limit + 1;

  const hastaRegistro = Math.min(
    page * limit,
    total
  );

  const modulosDisponibles = useMemo(() => {
    return Array.from(
      new Set(
        auditorias
          .map((item) => clean(item.modulo))
          .filter(Boolean)
      )
    ).sort((a, b) =>
      a.localeCompare(b, "es")
    );
  }, [auditorias]);

  const entidadesDisponibles = useMemo(() => {
    return Array.from(
      new Set(
        auditorias
          .map((item) => clean(item.entidad))
          .filter(Boolean)
      )
    ).sort((a, b) =>
      a.localeCompare(b, "es")
    );
  }, [auditorias]);

  const paginasVisibles = useMemo(() => {
    const paginas = [];

    const inicio = Math.max(
      1,
      page - 2
    );

    const fin = Math.min(
      totalPaginas,
      page + 2
    );

    for (
      let i = inicio;
      i <= fin;
      i += 1
    ) {
      paginas.push(i);
    }

    return paginas;
  }, [page, totalPaginas]);

  const usuarioSeleccionado = useMemo(() => {
    return usuariosDisponibles.find(
      (item) =>
        String(item.id) === String(usuario)
    );
  }, [usuariosDisponibles, usuario]);

  /* ------------------------------------------------------------------------ */
  /*                                Acciones UI                               */
  /* ------------------------------------------------------------------------ */

  const buscar = () => {
    cargarAuditorias({
      pagina: 1,
    });
  };

  const limpiarFiltros = () => {
    const nuevosFiltros = {
      desde: firstDayMonthISO(),
      hasta: todayLocalISO(),
      usuario: "",
      modulo: "",
      accion: "",
      entidad: "",
      resultado: "",
      q: "",
    };

    setDesde(nuevosFiltros.desde);
    setHasta(nuevosFiltros.hasta);
    setUsuario("");
    setModulo("");
    setAccion("");
    setEntidad("");
    setResultado("");
    setQ("");

    cargarAuditorias({
      pagina: 1,
      filtros: {
        ...nuevosFiltros,

        hasta: nuevosFiltros.hasta
          ? sumarDiasISO(
              nuevosFiltros.hasta,
              1
            )
          : "",
      },
    });
  };

  const cambiarPagina = (
    nuevaPagina
  ) => {
    if (
      nuevaPagina < 1 ||
      nuevaPagina > totalPaginas ||
      nuevaPagina === page
    ) {
      return;
    }

    cargarAuditorias({
      pagina: nuevaPagina,
    });
  };

  const cambiarLimite = (value) => {
    const nuevoLimite = Number(
      value || 50
    );

    setLimit(nuevoLimite);

    cargarAuditorias({
      pagina: 1,
      limite: nuevoLimite,
    });
  };

  /* ------------------------------------------------------------------------ */
  /*                                 Detalle                                  */
  /* ------------------------------------------------------------------------ */

  const abrirDetalle = async (id) => {
    setModalVisible(true);
    setDetalleLoading(true);
    setDetalleError("");
    setAuditoriaDetalle(null);

    try {
      const resp = await getAuditoria(id);

      setAuditoriaDetalle(
        resp?.auditoria || null
      );
    } catch (err) {
      console.error(
        "Error cargando detalle de auditoría:",
        err
      );

      setDetalleError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo consultar el detalle."
      );
    } finally {
      setDetalleLoading(false);
    }
  };

  const cerrarDetalle = () => {
    setModalVisible(false);
    setAuditoriaDetalle(null);
    setDetalleError("");
  };

  const cambiosDetalle = Array.isArray(
    auditoriaDetalle?.cambios
  )
    ? auditoriaDetalle.cambios
    : [];

  /* ------------------------------------------------------------------------ */
  /*                               Exportación                                */
  /* ------------------------------------------------------------------------ */

  const obtenerTodosLosRegistros =
    async () => {
      const filtros =
        obtenerFiltrosActuales();

      let paginaActual = 1;
      let registros = [];
      let totalRegistros = 0;

      do {
        const resp =
          await getAuditorias({
            page: paginaActual,
            limit: 200,
            ...filtros,
          });

        const lote = Array.isArray(
          resp?.auditorias
        )
          ? resp.auditorias
          : [];

        registros =
          registros.concat(lote);

        totalRegistros = Number(
          resp?.total ||
            lote.length ||
            0
        );

        paginaActual += 1;
      } while (
        registros.length <
        totalRegistros
      );

      return registros;
    };

  const exportarCSV = async () => {
    setExportando(true);
    setError("");

    try {
      const registros =
        await obtenerTodosLosRegistros();

      const encabezados = [
        "Fecha",
        "Usuario",
        "Email",
        "Rol",
        "Sede",
        "Módulo",
        "Entidad",
        "Acción",
        "Referencia",
        "Método",
        "Ruta",
        "Resultado",
        "Mensaje",
        "IP",
        "Cambios",
      ];

      const filas = registros.map(
        (item) => {
          const cambios = Array.isArray(
            item.cambios
          )
            ? item.cambios
                .map(
                  (cambio) =>
                    `${cambio.campo}: ${mostrarValor(
                      cambio.anterior
                    )} -> ${mostrarValor(
                      cambio.nuevo
                    )}`
                )
                .join(" | ")
            : "";

          return [
            formatDateTime(item.fecha),
            item.usuarioNombre,
            item.usuarioEmail,
            rolTexto(item.usuarioRole),
            item.sede,
            item.modulo,
            item.entidad,
            item.accion,
            item.referencia,
            item.metodo,
            item.ruta,
            item.resultado,
            item.mensaje,
            item.ip,
            cambios,
          ];
        }
      );

      const contenido = [
        encabezados
          .map(escaparCSV)
          .join(";"),

        ...filas.map((fila) =>
          fila
            .map(escaparCSV)
            .join(";")
        ),
      ].join("\n");

      const blob = new Blob(
        [`\uFEFF${contenido}`],
        {
          type: "text/csv;charset=utf-8;",
        }
      );

      const url =
        URL.createObjectURL(blob);

      const link =
        document.createElement("a");

      link.href = url;

      link.download = `auditoria-${
        desde || "inicio"
      }-${hasta || "actual"}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(
        "Error exportando CSV:",
        err
      );

      setError(
        err?.response?.data?.error ||
          err?.message ||
          "No se pudo exportar el CSV."
      );
    } finally {
      setExportando(false);
    }
  };

  const exportarPDF = async () => {
    setExportando(true);
    setError("");

    try {
      const registros =
        await obtenerTodosLosRegistros();

      const doc = new jsPDF(
        "landscape",
        "mm",
        "a4"
      );

      try {
        const logoImg =
          await cargarImagen(logo);

        doc.addImage(
          logoImg,
          "PNG",
          10,
          8,
          32,
          12
        );
      } catch {
        // El PDF continúa sin logo.
      }

      doc.setFont(
        "helvetica",
        "bold"
      );

      doc.setFontSize(15);

      doc.text(
        "Informe de Auditoría",
        48,
        14
      );

      doc.setFont(
        "helvetica",
        "normal"
      );

      doc.setFontSize(8);

      doc.text(
        `Desde: ${
          desde || "Todos"
        }  Hasta: ${
          hasta || "Todos"
        }`,
        48,
        20
      );

      doc.text(
        `Usuario: ${
          usuarioSeleccionado
            ?.usuarioNombre ||
          "Todos"
        } | Módulo: ${
          modulo || "Todos"
        } | Acción: ${
          accion || "Todas"
        }`,
        287,
        14,
        {
          align: "right",
        }
      );

      doc.text(
        `Registros: ${registros.length}`,
        287,
        20,
        {
          align: "right",
        }
      );

      const body = registros.map(
        (item) => [
          formatDateTimePdf(
            item.fecha
          ),
          item.usuarioNombre || "-",
          rolTexto(
            item.usuarioRole
          ),
          item.sede || "-",
          item.modulo || "-",
          item.entidad || "-",
          item.accion || "-",
          item.referencia || "-",
          item.resultado || "-",
          Array.isArray(item.cambios)
            ? String(
                item.cambios.length
              )
            : "0",
        ]
      );

      autoTable(doc, {
        startY: 26,
        theme: "grid",

        margin: {
          left: 6,
          right: 6,
          bottom: 10,
        },

        head: [
          [
            "Fecha",
            "Usuario",
            "Rol",
            "Sede",
            "Módulo",
            "Entidad",
            "Acción",
            "Referencia",
            "Resultado",
            "Cambios",
          ],
        ],

        body,

        styles: {
          fontSize: 6.5,
          cellPadding: 1,
          overflow: "linebreak",
          valign: "middle",
        },

        headStyles: {
          fillColor: [33, 37, 41],
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },

        alternateRowStyles: {
          fillColor: [
            248, 249, 250,
          ],
        },

        columnStyles: {
          0: {
            cellWidth: 23,
            halign: "center",
          },

          1: {
            cellWidth: 33,
          },

          2: {
            cellWidth: 18,
            halign: "center",
          },

          3: {
            cellWidth: 18,
            halign: "center",
          },

          4: {
            cellWidth: 27,
          },

          5: {
            cellWidth: 25,
          },

          6: {
            cellWidth: 24,
            halign: "center",
          },

          7: {
            cellWidth: 70,
          },

          8: {
            cellWidth: 20,
            halign: "center",
          },

          9: {
            cellWidth: 16,
            halign: "center",
          },
        },

        didParseCell: (data) => {
          if (
            data.section !== "body"
          ) {
            return;
          }

          if (
            data.column.index === 6
          ) {
            const value = clean(
              data.cell.raw
            ).toUpperCase();

            if (
              value ===
                "ELIMINACION" ||
              value === "BAJA"
            ) {
              data.cell.styles.textColor =
                [220, 53, 69];

              data.cell.styles.fontStyle =
                "bold";
            }

            if (
              value ===
                "MODIFICACION" ||
              value ===
                "ACTUALIZACION"
            ) {
              data.cell.styles.textColor =
                [13, 110, 253];

              data.cell.styles.fontStyle =
                "bold";
            }

            if (
              value === "ALTA" ||
              value === "CREACION"
            ) {
              data.cell.styles.textColor =
                [25, 135, 84];

              data.cell.styles.fontStyle =
                "bold";
            }

            if (
              value === "ANULACION"
            ) {
              data.cell.styles.textColor =
                [190, 130, 0];

              data.cell.styles.fontStyle =
                "bold";
            }
          }

          if (
            data.column.index === 8
          ) {
            const value = clean(
              data.cell.raw
            ).toUpperCase();

            data.cell.styles.fontStyle =
              "bold";

            data.cell.styles.textColor =
              value === "ERROR"
                ? [220, 53, 69]
                : [25, 135, 84];
          }
        },

        didDrawPage: (data) => {
          const pageHeight =
            doc.internal.pageSize.getHeight();

          doc.setFontSize(7);
          doc.setTextColor(100);

          doc.text(
            `Página ${data.pageNumber}`,
            287,
            pageHeight - 4,
            {
              align: "right",
            }
          );
        },
      });

      doc.save(
        `informe-auditoria-${
          desde || "inicio"
        }-${hasta || "actual"}.pdf`
      );
    } catch (err) {
      console.error(
        "Error exportando PDF:",
        err
      );

      setError(
        err?.response?.data?.error ||
          err?.message ||
          "No se pudo exportar el PDF."
      );
    } finally {
      setExportando(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                  */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="container-fluid mt-3 mb-4">
      <Card className="shadow-sm border-0">
        <Card.Body>
          {/* Encabezado */}

          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h4 className="mb-0">
                Informe de Auditoría
              </h4>

              <small className="text-muted">
                Consulta detallada de movimientos realizados en el sistema
              </small>
            </div>

            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={limpiarFiltros}
                disabled={
                  loading || exportando
                }
              >
                Limpiar
              </Button>

              <Button
                variant="dark"
                size="sm"
                onClick={buscar}
                disabled={
                  loading || exportando
                }
              >
                {loading
                  ? "Buscando..."
                  : "Buscar"}
              </Button>

              <Button
                variant="outline-success"
                size="sm"
                onClick={exportarCSV}
                disabled={
                  loading ||
                  exportando ||
                  total === 0
                }
              >
                CSV
              </Button>

              <Button
                variant="success"
                size="sm"
                onClick={exportarPDF}
                disabled={
                  loading ||
                  exportando ||
                  total === 0
                }
              >
                {exportando
                  ? "Exportando..."
                  : "PDF"}
              </Button>
            </div>
          </div>

          {/* Filtros */}

          <Row className="g-2 mb-3 align-items-end">
            <Col xs={12} sm={6} lg>
              <Form.Label>
                Desde
              </Form.Label>

              <Form.Control
                type="date"
                size="sm"
                value={desde}
                onChange={(e) =>
                  setDesde(
                    e.target.value
                  )
                }
              />
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Hasta
              </Form.Label>

              <Form.Control
                type="date"
                size="sm"
                value={hasta}
                onChange={(e) =>
                  setHasta(
                    e.target.value
                  )
                }
              />
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Usuario
              </Form.Label>

              <Form.Select
                size="sm"
                value={usuario}
                onChange={(e) =>
                  setUsuario(
                    e.target.value
                  )
                }
              >
                <option value="">
                  Todos los usuarios
                </option>

                {usuariosDisponibles.map(
                  (item) => (
                    <option
                      key={item.id}
                      value={item.id}
                    >
                      {item.usuarioNombre ||
                        item.usuarioEmail ||
                        "Usuario"}
                      {" - "}
                      {rolTexto(
                        item.usuarioRole
                      )}
                      {item.sede
                        ? ` / ${item.sede}`
                        : ""}
                      {Number(
                        item.cantidadMovimientos ||
                          0
                      ) > 0
                        ? ` (${item.cantidadMovimientos})`
                        : ""}
                    </option>
                  )
                )}
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Módulo
              </Form.Label>

              <Form.Control
                size="sm"
                value={modulo}
                list="auditoria-modulos"
                placeholder="Ej. PAGOS_VENTA"
                onChange={(e) =>
                  setModulo(
                    e.target.value
                  )
                }
              />

              <datalist id="auditoria-modulos">
                {modulosDisponibles.map(
                  (item) => (
                    <option
                      key={item}
                      value={item}
                    />
                  )
                )}
              </datalist>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Acción
              </Form.Label>

              <Form.Select
                size="sm"
                value={accion}
                onChange={(e) =>
                  setAccion(
                    e.target.value
                  )
                }
              >
                <option value="">
                  Todas
                </option>

                <option value="ALTA">
                  ALTA
                </option>

                <option value="MODIFICACION">
                  MODIFICACIÓN
                </option>

                <option value="ANULACION">
                  ANULACIÓN
                </option>

                <option value="ELIMINACION">
                  ELIMINACIÓN
                </option>
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Entidad
              </Form.Label>

              <Form.Control
                size="sm"
                value={entidad}
                list="auditoria-entidades"
                placeholder="Ej. PAGO_VENTA"
                onChange={(e) =>
                  setEntidad(
                    e.target.value
                  )
                }
              />

              <datalist id="auditoria-entidades">
                {entidadesDisponibles.map(
                  (item) => (
                    <option
                      key={item}
                      value={item}
                    />
                  )
                )}
              </datalist>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Resultado
              </Form.Label>

              <Form.Select
                size="sm"
                value={resultado}
                onChange={(e) =>
                  setResultado(
                    e.target.value
                  )
                }
              >
                <option value="">
                  Todos
                </option>

                <option value="OK">
                  OK
                </option>

                <option value="ERROR">
                  ERROR
                </option>
              </Form.Select>
            </Col>

            <Col xs={12} lg={10}>
              <Form.Label>
                Buscar
              </Form.Label>

              <Form.Control
                size="sm"
                value={q}
                placeholder="Usuario, email, referencia, registro, mensaje o ruta..."
                onChange={(e) =>
                  setQ(e.target.value)
                }
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter"
                  ) {
                    buscar();
                  }
                }}
              />
            </Col>

            <Col xs={12} lg={2}>
              <Form.Label>
                Registros por página
              </Form.Label>

              <Form.Select
                size="sm"
                value={limit}
                onChange={(e) =>
                  cambiarLimite(
                    e.target.value
                  )
                }
              >
                <option value={25}>
                  25
                </option>

                <option value={50}>
                  50
                </option>

                <option value={100}>
                  100
                </option>

                <option value={200}>
                  200
                </option>
              </Form.Select>
            </Col>
          </Row>

          {error && (
            <div className="alert alert-danger py-2">
              {error}
            </div>
          )}

          {/* Totales generales */}

          <Row className="g-2 mb-3">
            <Col xs={6} md>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Total movimientos
                  </small>

                  <h5 className="mb-0">
                    {resumen.total}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={6} md>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Altas
                  </small>

                  <h5 className="mb-0 text-success">
                    {resumen.altas}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={6} md>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Modificaciones
                  </small>

                  <h5 className="mb-0 text-primary">
                    {
                      resumen.modificaciones
                    }
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={6} md>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Anulaciones
                  </small>

                  <h5 className="mb-0 text-warning">
                    {
                      resumen.anulaciones
                    }
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={6} md>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Eliminaciones
                  </small>

                  <h5 className="mb-0 text-danger">
                    {
                      resumen.eliminaciones
                    }
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={6} md>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Errores
                  </small>

                  <h5 className="mb-0 text-danger">
                    {resumen.errores}
                  </h5>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tabla */}

          <div
            className="table-responsive"
            style={{
              maxHeight: "64vh",
            }}
          >
            <Table
              striped
              bordered
              hover
              size="sm"
              className="align-middle"
            >
              <thead
                className="table-dark"
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                }}
              >
                <tr>
                  <th>
                    Fecha y hora
                  </th>

                  <th>Usuario</th>
                  <th>Rol / sede</th>
                  <th>Módulo</th>
                  <th>Entidad</th>
                  <th>Acción</th>
                  <th>Referencia</th>
                  <th>Método</th>
                  <th>Resultado</th>

                  <th className="text-center">
                    Cambios
                  </th>

                  <th className="text-center">
                    Detalle
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="11"
                      className="text-center py-4"
                    >
                      <Spinner
                        animation="border"
                        size="sm"
                      />{" "}
                      Cargando auditoría...
                    </td>
                  </tr>
                ) : auditorias.length ===
                  0 ? (
                  <tr>
                    <td
                      colSpan="11"
                      className="text-center py-4 text-muted"
                    >
                      No existen movimientos para los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  auditorias.map(
                    (item) => (
                      <tr key={item._id}>
                        <td
                          style={{
                            whiteSpace:
                              "nowrap",
                          }}
                        >
                          {formatDateTime(
                            item.fecha
                          )}
                        </td>

                        <td>
                          <div className="fw-bold">
                            {item.usuarioNombre ||
                              "Sin usuario"}
                          </div>

                          <small className="text-muted">
                            {item.usuarioEmail ||
                              "-"}
                          </small>
                        </td>

                        <td>
                          <div>
                            {rolTexto(
                              item.usuarioRole
                            )}
                          </div>

                          <small className="text-muted">
                            {item.sede ||
                              "-"}
                          </small>
                        </td>

                        <td className="fw-bold">
                          {item.modulo ||
                            "-"}
                        </td>

                        <td>
                          {item.entidad ||
                            "-"}
                        </td>

                        <td>
                          <Badge
                            bg={accionBadge(
                              item.accion
                            )}
                          >
                            {item.accion ||
                              "-"}
                          </Badge>
                        </td>

                        <td
                          style={{
                            minWidth: 220,
                          }}
                        >
                          {item.referencia ||
                            "-"}
                        </td>

                        <td>
                          <Badge
                            bg="secondary"
                            className="fw-normal"
                          >
                            {item.metodo ||
                              "-"}
                          </Badge>
                        </td>

                        <td>
                          <Badge
                            bg={resultadoBadge(
                              item.resultado
                            )}
                          >
                            {item.resultado ||
                              "-"}
                          </Badge>
                        </td>

                        <td className="text-center fw-bold">
                          {Array.isArray(
                            item.cambios
                          )
                            ? item.cambios
                                .length
                            : 0}
                        </td>

                        <td className="text-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              abrirDetalle(
                                item._id
                              )
                            }
                          >
                            Ver detalle
                          </Button>
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </Table>
          </div>

          {/* Paginación */}

          {total > 0 && (
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
              <small className="text-muted">
                Mostrando{" "}
                {desdeRegistro} a{" "}
                {hastaRegistro} de{" "}
                {total} registros
              </small>

              <Pagination
                size="sm"
                className="mb-0"
              >
                <Pagination.First
                  disabled={page === 1}
                  onClick={() =>
                    cambiarPagina(1)
                  }
                />

                <Pagination.Prev
                  disabled={page === 1}
                  onClick={() =>
                    cambiarPagina(
                      page - 1
                    )
                  }
                />

                {paginasVisibles.map(
                  (numeroPagina) => (
                    <Pagination.Item
                      key={numeroPagina}
                      active={
                        numeroPagina ===
                        page
                      }
                      onClick={() =>
                        cambiarPagina(
                          numeroPagina
                        )
                      }
                    >
                      {numeroPagina}
                    </Pagination.Item>
                  )
                )}

                <Pagination.Next
                  disabled={
                    page === totalPaginas
                  }
                  onClick={() =>
                    cambiarPagina(
                      page + 1
                    )
                  }
                />

                <Pagination.Last
                  disabled={
                    page === totalPaginas
                  }
                  onClick={() =>
                    cambiarPagina(
                      totalPaginas
                    )
                  }
                />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal detalle */}

      <Modal
        show={modalVisible}
        onHide={cerrarDetalle}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Detalle de auditoría
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {detalleLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />

              <div className="mt-2">
                Cargando detalle...
              </div>
            </div>
          ) : detalleError ? (
            <div className="alert alert-danger">
              {detalleError}
            </div>
          ) : auditoriaDetalle ? (
            <>
              <Row className="g-2 mb-3">
                <Col md={3}>
                  <Card className="h-100">
                    <Card.Body className="py-2">
                      <small className="text-muted">
                        Fecha
                      </small>

                      <div className="fw-bold">
                        {formatDateTime(
                          auditoriaDetalle.fecha
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={3}>
                  <Card className="h-100">
                    <Card.Body className="py-2">
                      <small className="text-muted">
                        Usuario
                      </small>

                      <div className="fw-bold">
                        {auditoriaDetalle.usuarioNombre ||
                          "-"}
                      </div>

                      <small>
                        {auditoriaDetalle.usuarioEmail ||
                          "-"}
                      </small>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={2}>
                  <Card className="h-100">
                    <Card.Body className="py-2">
                      <small className="text-muted">
                        Rol / sede
                      </small>

                      <div className="fw-bold">
                        {rolTexto(
                          auditoriaDetalle.usuarioRole
                        )}
                      </div>

                      <small>
                        {auditoriaDetalle.sede ||
                          "-"}
                      </small>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={2}>
                  <Card className="h-100">
                    <Card.Body className="py-2">
                      <small className="text-muted">
                        Acción
                      </small>

                      <div className="mt-1">
                        <Badge
                          bg={accionBadge(
                            auditoriaDetalle.accion
                          )}
                        >
                          {auditoriaDetalle.accion ||
                            "-"}
                        </Badge>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={2}>
                  <Card className="h-100">
                    <Card.Body className="py-2">
                      <small className="text-muted">
                        Resultado
                      </small>

                      <div className="mt-1">
                        <Badge
                          bg={resultadoBadge(
                            auditoriaDetalle.resultado
                          )}
                        >
                          {auditoriaDetalle.resultado ||
                            "-"}
                        </Badge>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card className="mb-3">
                <Card.Body>
                  <Row className="g-2">
                    <Col md={6}>
                      <small className="text-muted">
                        Referencia
                      </small>

                      <div className="fw-bold">
                        {auditoriaDetalle.referencia ||
                          "-"}
                      </div>
                    </Col>

                    <Col md={3}>
                      <small className="text-muted">
                        Módulo / entidad
                      </small>

                      <div>
                        {auditoriaDetalle.modulo ||
                          "-"}{" "}
                        /{" "}
                        {auditoriaDetalle.entidad ||
                          "-"}
                      </div>
                    </Col>

                    <Col md={3}>
                      <small className="text-muted">
                        Método y ruta
                      </small>

                      <div>
                        {auditoriaDetalle.metodo ||
                          "-"}{" "}
                        {auditoriaDetalle.ruta ||
                          "-"}
                      </div>
                    </Col>

                    <Col md={6}>
                      <small className="text-muted">
                        Mensaje
                      </small>

                      <div>
                        {auditoriaDetalle.mensaje ||
                          "-"}
                      </div>
                    </Col>

                    <Col md={3}>
                      <small className="text-muted">
                        IP
                      </small>

                      <div>
                        {auditoriaDetalle.ip ||
                          "-"}
                      </div>
                    </Col>

                    <Col md={3}>
                      <small className="text-muted">
                        ID entidad
                      </small>

                      <div
                        style={{
                          wordBreak:
                            "break-all",
                        }}
                      >
                        {auditoriaDetalle.entidadId ||
                          "-"}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <h6>
                Cambios detectados
              </h6>

              <div className="table-responsive mb-3">
                <Table
                  bordered
                  striped
                  size="sm"
                  className="align-middle"
                >
                  <thead className="table-dark">
                    <tr>
                      <th>Campo</th>
                      <th>
                        Valor anterior
                      </th>
                      <th>
                        Valor nuevo
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {cambiosDetalle.length ===
                    0 ? (
                      <tr>
                        <td
                          colSpan="3"
                          className="text-center text-muted py-3"
                        >
                          No se registraron cambios individuales.
                        </td>
                      </tr>
                    ) : (
                      cambiosDetalle.map(
                        (
                          cambio,
                          index
                        ) => (
                          <tr
                            key={`${cambio.campo}-${index}`}
                          >
                            <td className="fw-bold">
                              {cambio.campo ||
                                "-"}
                            </td>

                            <td
                              className="text-danger"
                              style={{
                                wordBreak:
                                  "break-word",
                              }}
                            >
                              {mostrarValor(
                                cambio.anterior
                              )}
                            </td>

                            <td
                              className="text-success"
                              style={{
                                wordBreak:
                                  "break-word",
                              }}
                            >
                              {mostrarValor(
                                cambio.nuevo
                              )}
                            </td>
                          </tr>
                        )
                      )
                    )}
                  </tbody>
                </Table>
              </div>

              <Row className="g-3">
                <Col lg={6}>
                  <h6>
                    Datos anteriores
                  </h6>

                  <pre
                    className="border rounded p-3"
                    style={{
                      maxHeight: 320,
                      overflow: "auto",
                      fontSize:
                        "0.78rem",
                      background:
                        "rgba(127,127,127,0.06)",
                    }}
                  >
                    {jsonBonito(
                      auditoriaDetalle.datosAntes
                    )}
                  </pre>
                </Col>

                <Col lg={6}>
                  <h6>
                    Datos posteriores
                  </h6>

                  <pre
                    className="border rounded p-3"
                    style={{
                      maxHeight: 320,
                      overflow: "auto",
                      fontSize:
                        "0.78rem",
                      background:
                        "rgba(127,127,127,0.06)",
                    }}
                  >
                    {jsonBonito(
                      auditoriaDetalle.datosDespues
                    )}
                  </pre>
                </Col>

                <Col lg={6}>
                  <h6>Metadata</h6>

                  <pre
                    className="border rounded p-3"
                    style={{
                      maxHeight: 280,
                      overflow: "auto",
                      fontSize:
                        "0.78rem",
                      background:
                        "rgba(127,127,127,0.06)",
                    }}
                  >
                    {jsonBonito(
                      auditoriaDetalle.metadata
                    )}
                  </pre>
                </Col>

                <Col lg={6}>
                  <h6>Navegador</h6>

                  <div
                    className="border rounded p-3"
                    style={{
                      minHeight: 90,
                      wordBreak:
                        "break-word",
                      background:
                        "rgba(127,127,127,0.06)",
                    }}
                  >
                    {auditoriaDetalle.userAgent ||
                      "-"}
                  </div>
                </Col>
              </Row>
            </>
          ) : null}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={cerrarDetalle}
          >
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}